/**
 * LAYOUT EDITOR — pure state transitions behind the intake form's inline "Edit layout"
 * mode (see components/LayoutEditor.jsx and pages/IntakeForm.jsx).
 *
 * Edit mode works on a DRAFT copy of `settings/fieldLayout` and renders the form from it
 * live, so an admin sees exactly what attorneys will see. Every transition here returns a
 * NEW draft (no mutation) in the same document shape the Admin Field Builder writes:
 *
 *   base field edits  → draft.overrides[path] = { isActive: true, ...patch }
 *   custom field edits→ the field's own entry in draft.customFields
 *   section renames   → draft.sectionOverrides[id].title
 *
 * Keeping these transitions pure (no React, no Firestore) makes the tricky parts —
 * reordering a mixed base/custom section, moving across pages — unit-testable in
 * isolation (layoutEditor.test.js), mirroring the fieldLayout.js/AdminFieldBuilder split.
 */

import { normalizeLayout, sanitizeLayoutOptions, DEFAULT_FIELD_LAYOUT } from './fieldLayout.js';

/** A deep-cloned, normalized draft of the stored layout document (or the empty default). */
export function createDraft(stored) {
  return normalizeLayout(structuredClone(stored || DEFAULT_FIELD_LAYOUT));
}

/**
 * Apply a partial edit to one field, routing it to the right home: a base field's
 * override record, or the custom field's own entry. Patch keys are the shared names the
 * builder already uses (label, width, isActive, helpText, requirement, newRow, fullWidth,
 * sectionId, order, text, variant, visibleIf) — mergeBaseField/mergeCustomField translate
 * them into effective-field props.
 */
export function patchField(draft, field, patch) {
  if (field.isCustom) {
    return {
      ...draft,
      customFields: draft.customFields.map((custom) =>
        custom.path === field.path ? { ...custom, ...patch } : custom
      ),
    };
  }
  const existing = draft.overrides[field.path] || {};
  return {
    ...draft,
    overrides: {
      ...draft.overrides,
      [field.path]: { isActive: true, ...existing, ...patch },
    },
  };
}

/**
 * Write an explicit `order: index` for every field in a section, in the given array
 * order. Used after any reorder so the whole section's ordering is pinned — mixing
 * explicit and implicit orders is how fields "jump around" after later inserts.
 */
export function pinSectionOrder(draft, sectionFields) {
  return sectionFields.reduce(
    (next, field, index) => patchField(next, field, { order: index }),
    draft
  );
}

/**
 * Move a field one slot up (-1) or down (+1) within its section. `sectionFields` is the
 * section's current builder-order field array. Fields without a path (legacy pinned
 * blocks) can't be addressed by an override, so they are left where they are and the
 * move skips over them. Returns the draft unchanged when the move would fall off an end.
 */
export function moveFieldWithinSection(draft, sectionFields, field, direction) {
  const movable = sectionFields.filter((f) => f.path);
  const from = movable.findIndex((f) => f.path === field.path);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= movable.length) return draft;
  const reordered = [...movable];
  const [entry] = reordered.splice(from, 1);
  reordered.splice(to, 0, entry);
  return pinSectionOrder(draft, reordered);
}

/**
 * Send a field to the end of another page. The caller passes the target section's
 * current builder field array so the new order lands after its last entry.
 */
export function moveFieldToSection(draft, field, targetSectionId, targetSectionFields = []) {
  const maxOrder = targetSectionFields.reduce(
    (max, f) => (Number.isFinite(f.order) ? Math.max(max, f.order) : max),
    targetSectionFields.length - 1
  );
  return patchField(draft, field, { sectionId: targetSectionId, order: maxOrder + 1 });
}

/** Rename a page inline. An empty title falls back to the coded/custom title at merge. */
export function renameSection(draft, sectionId, title) {
  const existing = draft.sectionOverrides[sectionId] || {};
  return {
    ...draft,
    sectionOverrides: {
      ...draft.sectionOverrides,
      [sectionId]: { ...existing, title },
    },
  };
}

/**
 * Set (or clear) a page's show/hide condition.
 *
 * `condition === undefined` REMOVES the override, restoring whatever the code schema
 * ships. `condition === true` is a real, stored value meaning "always show" — that is how
 * an admin overrides a coded condition that references a field they have removed, which
 * would otherwise hide the page forever with no way back.
 */
export function setSectionCondition(draft, sectionId, condition) {
  const existing = draft.sectionOverrides[sectionId] || {};
  const next =
    condition === undefined
      ? (() => {
          const { visibleIf, ...rest } = existing;
          return rest;
        })()
      : { ...existing, visibleIf: condition };
  return {
    ...draft,
    sectionOverrides: { ...draft.sectionOverrides, [sectionId]: next },
  };
}

/** Edit a page's description. An empty string deliberately clears a coded description. */
export function describeSection(draft, sectionId, description) {
  const existing = draft.sectionOverrides[sectionId] || {};
  return {
    ...draft,
    sectionOverrides: {
      ...draft.sectionOverrides,
      [sectionId]: { ...existing, description },
    },
  };
}

/**
 * Delete ANY field. A custom field's record is removed outright. A base field cannot be
 * removed from the code schema, so it gets a `deleted: true` tombstone in its override —
 * the merge then drops it from both the live form and the builder view, which is a true
 * delete from the admin's perspective. Recoverable from the Field Builder's "Deleted
 * fields" panel (restoreBaseField); the callers are responsible for the named
 * are-you-sure warning when the field is referenced by approval rules or conditions.
 */
export function deleteField(draft, field) {
  if (field.isCustom) {
    return {
      ...draft,
      customFields: draft.customFields.filter((custom) => custom.path !== field.path),
    };
  }
  return patchField(draft, field, { deleted: true });
}

/** Back-compat alias (custom fields only) — prefer deleteField. */
export function deleteCustomField(draft, field) {
  if (!field.isCustom) return draft;
  return deleteField(draft, field);
}

/** Bring a deleted base field back (its other overrides — label, width… — survive). */
export function restoreBaseField(draft, path) {
  const existing = draft.overrides[path];
  if (!existing || existing.deleted !== true) return draft;
  const { deleted, ...rest } = existing;
  return { ...draft, overrides: { ...draft.overrides, [path]: rest } };
}

/**
 * The payload `adminUpdateSettings` expects for a save, with a bumped version. Option
 * lists are sanitized on the way out (see fieldLayout.js sanitizeLayoutOptions): blank or
 * duplicate rows are dropped, and a base override emptied of options reverts the field to
 * its code-shipped choices instead of shipping an unsatisfiable field.
 */
export function toSavePayload(draft, storedVersion) {
  const sanitized = sanitizeLayoutOptions(draft);
  return {
    version: (Number.isFinite(storedVersion) ? storedVersion : 0) + 1,
    overrides: sanitized.overrides,
    customFields: sanitized.customFields,
    customSections: sanitized.customSections,
    sectionOverrides: sanitized.sectionOverrides,
  };
}
